import clsx from 'clsx';
import styles from './checkbox.module.css';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { useField } from '@/components/form';
import { useTranslation } from '@/contexts/i18n-context';
import { ReactNode } from 'react';

export type CheckboxVariant = 'classic' | 'selector';
interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string | ReactNode;
  name: string;
  error?: string;
  variant?: CheckboxVariant;
  testId?: string;
}

export function Checkbox({
  label,
  name,
  onChange,
  error,
  variant = 'classic',
  testId,
  ...restProps
}: CheckboxProps) {
  const { fieldProps, error: fieldError } = useField(name, restProps);
  const { t, isTranslationKey } = useTranslation();
  const fieldErrorMessage =
    fieldError?.message && isTranslationKey(fieldError.message)
      ? t(fieldError.message)
      : fieldError?.message;

  const displayError = error || fieldErrorMessage;

  return (
    <div
      className={clsx(
        styles.checkbox,
        variant !== 'classic' && styles[variant],
        fieldProps.className,
      )}
    >
      <input
        {...fieldProps}
        onChange={(e) => {
          fieldProps.onChange(e);
          onChange?.(e);
        }}
        className={styles.checkboxInput}
        type="checkbox"
        id={name}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <label className={styles.controlLabel} htmlFor={name} data-testid={testId}>
        <MaterialSymbolIcon name="check" fill={true} className={styles.checkIcon} />
        {typeof label === 'string' ? <span>{label}</span> : label}
        {displayError && <p className={styles.error}>{displayError}</p>}
      </label>
    </div>
  );
}
